/* eslint-disable @typescript-eslint/naming-convention */
import { Location as LocationAngularCommon, TitleCasePipe } from "@angular/common";
import { Component, OnInit, ViewChild } from "@angular/core";
import { Validators } from "@angular/forms";
import { MatDialog, MatDialogRef } from "@angular/material/dialog";
import { MatTabGroup } from "@angular/material/tabs";
import { ActivatedRoute, Router } from "@angular/router";
import * as moment from "moment";
import { finalize } from "rxjs/operators";
import { yesNoOptions } from "src/app/shared/constants/components";
import { AuthenticationService } from "../../../../core/authentication/authentication.service";
import { PaginatedResponse } from "../../../../core/http/paginated-response.type";
import { LoadingService } from "../../../../core/loader/loading.service";
import { DataTableComponent } from "../../../../shared/components/data-table/data-table.component";
import {
  DataTable,
  DataTableParamsWhere,
  GetDataEvent,
  UpdateDataEvent,
} from "../../../../shared/components/data-table/data-table.type";
import { DeleteModalComponent } from "../../../../shared/components/delete-modal/delete-modal.component";
import {
  DynamicFormComponent,
  DynamicFormCustomBtn,
} from "../../../../shared/components/dynamic-form/dynamic-form.component";
import { QuestionBase } from "../../../../shared/components/dynamic-form/question-base";
import { GenericModalComponent } from "../../../../shared/components/generic-modal/generic-modal.component";
import { GenericModalData } from "../../../../shared/components/generic-modal/generic-modal.type";
import { NotificationService } from "../../../../shared/components/notification/notification.service";
import { PropertyListComponent } from "../../../../shared/components/property-list/property-list.component";
import { Operator, PropertyList } from "../../../../shared/components/property-list/property-list.type";
import { FORMAT } from "../../../../shared/constants/formats";
import { pattern } from "../../../../shared/constants/patterns";
import { VALUE } from "../../../../shared/constants/values";
import { MomentPipe } from "../../../../shared/pipes/moment/moment.pipe";
import { AnyObject } from "../../../../shared/types/app.type";
import { UserInfo } from "../../../../shared/types/user-info";
import { getUpdatedValues } from "../../../../shared/utils/helpers/common.helper";
import { isBetweenValidator } from "../../../../shared/validators/is-between.validator";
import { getDisplayedColumnsAssignAssets } from "../../assets/asset";
import { AssetTableService } from "../../assets/asset-table.service";
import { Asset, TotalContractAssetsPrices } from "../../assets/asset.type";
import { ConfigurableOptionService } from "../../configurable-options/configurable-options.service";
import { ConfigurableOption, ConfigurableOptionTypes } from "../../configurable-options/configurable-options.type";
import { MaintenanceProvider } from "../../maintenance-provider/maintenance-provider.types";
import { CreateQuoteService } from "../create-quote/create-quote.service";
import { QuoteService } from "../quote.service";
import { BulkUpdateQuotedAssetDto, Quote, QuotedAsset } from "../quote.type";
import { getQuoteAssetSearchFilterOptions } from "../quoted-asset";

@Component({
  selector: "app-manage-quote",
  templateUrl: "./manage-quote.component.html",
  styleUrls: ["./manage-quote.component.scss"],
  providers: [CreateQuoteService, TitleCasePipe],
})
export class ManageQuoteComponent implements OnInit {
  @ViewChild(DataTableComponent) assetsTable: DataTableComponent<Asset>;
  @ViewChild(DynamicFormComponent) dynamicForm: DynamicFormComponent;
  @ViewChild(MatTabGroup) tabGroup: MatTabGroup;

  currentUser: UserInfo;
  isEdit = false;
  quote: Quote;
  quoteId: string;
  location: Location;
  maintenanceProviders: MaintenanceProvider[];
  questions: QuestionBase<string | boolean | number>[] = [];
  totalAssetsPrices: TotalContractAssetsPrices;
  assetsTableConfig: DataTable<Asset>;
  convertQuoteBtn: DynamicFormCustomBtn = {
    label: "Convert",
    disabled: true,
    onClick: () => this.convertQuoteAction(),
  };

  constructor(
    public dialog: MatDialog,
    public assetTableService: AssetTableService,
    private configurableOptionService: ConfigurableOptionService,
    public dialogRef: MatDialogRef<GenericModalComponent | DeleteModalComponent>,
    public locationAngularCommon: LocationAngularCommon,
    private router: Router,
    private route: ActivatedRoute,
    private notificationService: NotificationService,
    private loader: LoadingService,
    private questionsService: CreateQuoteService,
    private quoteService: QuoteService,
    private authenticationService: AuthenticationService,
  ) {
    this.currentUser = this.authenticationService.currentUserInfo;
  }

  //#region Lifecycle Methods
  ngOnInit(): void {
    this.quoteId = this.route.snapshot.paramMap.get("id");
    this.getQuote({ reloadForm: true, setAssetsTab: true });
    this.setAssetsTab();
  }

  //#endregion

  //#region Handlers
  onGoBack = (): void => this.goBack();

  onToggleManageQuote = (): void => this.toggleManageQuote();

  onUpdateQuote = (): void => {
    if (this.dynamicForm.form.valid) {
      this.updateQuote();
    }
  };

  onChangeQuote = (event: AnyObject): void => {
    if (event.key === "startDate") {
      this.dynamicForm.getQuestion("endDate").minDate = event.value;
    }
    if (event.key === "endDate") {
      const startDateSelected = this.dynamicForm.getQuestion("startDate").value as string;
      const endDateSelected = event.value;
      this.dynamicForm.replaceOrAppendQuestion(
        this.questionsService.createDateQuestion({ startDateSelected, endDateSelected }),
      );
      this.dynamicForm.getQuestion("startDate").maxDate = event.value;
    }
  };

  onDeleteQuote = (): void => this.deleteQuote();

  /** @workaround fix an issue when table columns are not displayed properly in the table */
  onTabChange = (): void => this.resetTabs();

  //#endregion

  //#region Getters
  /** Populate form data */
  getQuote = (options?: { reloadForm: boolean; setAssetsTab?: boolean }): void => {
    this.quoteService
      .getQuote(this.quoteId, {
        relations: { maintenanceProvider: true, quotedAssets: true, contract: true, company: { partner: true } },
      })
      .subscribe(
        (quote) => {
          this.quote = quote;
          if (options?.reloadForm) {
            this.buildForm(this.quote);
          }
          if (options?.setAssetsTab) {
            this.assetsTableConfig.toolBar.addItem.hidden = !this.canModifyAssets();
            this.assetsTableConfig.toolBar.bulkEdit.hidden = !this.canModifyAssets();
            this.assetsTableConfig.toolBar.selectAllRows.hidden = !this.canModifyAssets();
            this.assetsTableConfig.selection.hidden = !this.canModifyAssets();
          }
          this.toggleConvertButton();
        },
        (error) => {
          this.router.navigate(["/quotes"]);
          this.notificationService.error(error || "Couldn't find Quote");
        },
      );
  };

  /** Populate assets table */
  getQuotedAssets = (event: GetDataEvent<QuotedAsset>): void => {
    const { onError, onResult, queryParams } = event;
    this.quoteService
      .getQuotedAssets({
        ...queryParams,
        where: {
          ...queryParams?.where,
          quoteId: this.quoteId,
        },
        relations: {
          sla: true,
          asset: {
            company: true,
            contract: true,
            location: true,
            internalProvider: true,
            type: { model: { oem: true } },
          },
        },
      })
      .subscribe(
        (data): void => {
          data.items = this.mapQuotedAssetToDisplayedAsset(data);
          this.toggleConvertButton();
          onResult(data);
        },
        (error): void => {
          onError(error);
        },
      );
  };

  updateQuotedAsset = (event: UpdateDataEvent<Asset>): void => {
    const { onError, onResult, row, updatedValues } = event;
    if (row.id && updatedValues) {
      this.loader.show();
      // If startDate is updated then we have to pass its endDate too
      // else if endDate is updated then we have to pass its startDate too
      if (updatedValues.startDate) {
        updatedValues.endDate = row.endDate;
      } else if (updatedValues.endDate) {
        updatedValues.startDate = row.startDate;
      }
      this.quoteService
        .updateQuotedAsset(row.quotedAssetId, { ...updatedValues })
        .pipe(finalize(() => this.dynamicForm.enable()))
        .subscribe(
          (data): void => {
            onResult(data);
            this.loader.hide();
          },
          (error): void => {
            onError(error);
            this.loader.hide();
          },
        );
    } else {
      onError();
    }
  };

  toggleConvertButton(): void {
    let warnMessage = "";
    let disabled = false;
    if (!!this.quote?.contractId) {
      this.convertQuoteBtn.disabled = true;
      this.convertQuoteBtn.warnMessage = "This quote has already been converted to a contract.";
      return;
    }
    if (!this.quote?.maintenanceProviderId) {
      warnMessage = `${warnMessage} Maintenance provider is required to convert a quote. \n`;
      disabled = true;
    }
    if (!this.quote?.quotedAssets?.length) {
      warnMessage = `${warnMessage} A quote must have assigned assets in order to be converted to a contract. \n`;
      disabled = true;
    }
    this.convertQuoteBtn.warnMessage = warnMessage;
    this.convertQuoteBtn.disabled = disabled;
  }

  mapQuotedAssetToDisplayedAsset(data: PaginatedResponse<QuotedAsset>) {
    if (!data || !data.items.length) {
      return [];
    }

    return data.items.reduce((arr, elem) => {
      const asset: Asset = {
        id: elem.assetId,
        quotedAssetId: elem.id, // required to update props
        name: elem.asset.name,
        serialNumber: elem.asset.serialNumber,
        description: elem.asset.description,
        sla: elem.sla,
        category: elem.asset.category,
        type: elem.asset.type,
        dmr: elem.dmr,
        monitoring: elem.monitoring,
        company: elem.asset.company,
        contract: elem.asset.contract,
        internalProvider: elem.asset.internalProvider,
        coverageStatus: elem.asset.coverageStatus,
        location: elem.asset.location,
        hasConfigurationNotes: elem.asset.hasConfigurationNotes,
        startDate: elem.startDate,
        endDate: this.quote.endDate,
        channelWorksCost: elem.channelWorksCost,
        partnerPrice: elem.partnerPrice,
      };
      return [...arr, asset];
    }, []);
  }
  //#endregion

  //#region Component Helpers

  getManageFormClass = (): string => {
    const partialClass = "manage-quote-form-";
    if (this.currentUser.isGlobal()) {
      return `${partialClass}global`;
    }
  };

  /** Check if can link, unlink or edit an Asset */
  canModifyAssets = (): boolean => {
    return !this.currentUser.hasRight(["viewer"]) && !this.quote?.contractId;
  };

  //#endregion

  //#region Asset Tab

  private setAssetsTab = (): void => {
    const editOnly = ["sla.value", "startDate", "channelWorksCost", "partnerPrice", "dmr", "monitoring"];
    this.assetsTableConfig = this.assetTableService.getAssetsTableConfig({
      entities: { quote: this.quote },
      selection: { hidden: false },
      actionColumn: { delete: { hidden: true } },
      inlineEdit: { editOnly },
      hidden: [
        "ticket",
        "oem",
        "company.name",
        "company.partner.name",
        "quotedPrice",
        "quotedSla",
        "quotedTerm",
        "startDate",
        "endDate",
        "isPending",
        "fairMarketValue",
        "bookValue",
      ],
      toolBar: {
        selectAllRows: {
          hidden: false,
        },
        exportCsv: {
          hidden: true,
        },
        addItem: {
          hidden: !this.canModifyAssets(),
          function: this.addAssets,
        },
        bulkEdit: {
          hidden: !this.canModifyAssets(),
          function: this.updateAssets,
        },
      },
    });

    this.assetsTableConfig = {
      ...this.assetsTableConfig,
      columns: [
        ...this.assetsTableConfig.columns,
        {
          prop: "startDate",
          name: "Start Date",
          inlineEdit: {
            control: "date",
            validators: (asset) => [
              isBetweenValidator(
                asset.quotedAsset?.quote?.startDate || VALUE.DEFAULT_MIN_DATE,
                asset.endDate || VALUE.DEFAULT_MAX_DATE,
                "[]",
              ),
            ],
            date: {
              max: (asset) => moment(asset.endDate || VALUE.DEFAULT_MAX_DATE).toDate(),
              min: (asset) => moment(asset.quotedAsset?.quote?.startDate || VALUE.DEFAULT_MIN_DATE).toDate(),
            },
            disabled: () => false,
          },
          pipes: [{ clazz: MomentPipe, args: [FORMAT.DATE] }],
        },
        {
          prop: "endDate",
          name: "End Date",
          pipes: [{ clazz: MomentPipe, args: [FORMAT.DATE] }],
        },
      ],
      actionColumn: [
        ...this.assetsTableConfig.actionColumn,
        {
          icon: "link_off",
          text: "Unlink",
          function: this.unlinkAsset,
          hidden: !this.canModifyAssets(),
        },
      ],
      searchBar: [
        {
          availableSearchFields: getQuoteAssetSearchFilterOptions(),
        },
      ],
    };
    const columns = this.assetsTableConfig.columns;
    this.assetsTableConfig.columns = columns.map((col) => {
      const canEdit = editOnly.includes(col.prop);
      return {
        ...col,
        inlineEdit: {
          ...col.inlineEdit,
          disabled: () => !canEdit || !!this.quote?.contractId,
        },
      };
    });
  };

  private addAssets = (table: DataTableComponent<Asset>): void => {
    // Assign companies to the user (used in the popup table)
    const action = (assetsTable: DataTableComponent<Asset>) => {
      if (assetsTable.config.selection.selectedRows.length) {
        this.loader.show();
        const body = { assetIds: assetsTable.config.selection.selectedRows.map((asset) => asset.id) };

        this.quoteService
          .linkAssets(this.quote.id, body)
          .subscribe(
            () => {
              this.notificationService.success("Asset was successfully assigned to a quote");
              this.getQuote();
              table.search();
            },
            (error) => this.notificationService.error(`Couldn't link assets to quote: ${error}`),
          )
          .add(() => this.loader.hide());
      }
    };

    this.dialog.open<GenericModalComponent, GenericModalData<{ config: DataTable<Asset> }>>(GenericModalComponent, {
      width: "1400px",
      panelClass: "custom-dialog-container",
      data: {
        title: `Link assets to ${this.quote.name}`,
        buttons: { hide: false, submit: { action } },
        component: {
          clazz: DataTableComponent,
          inputs: {
            config: this.assetTableService.getAssetsTableConfig({
              hidden: ["partner.name"],
              actionColumn: { hidden: true },
              inlineEdit: { disabled: () => true },
              syncWithQueryParams: false,
              selection: { hidden: false },
              shown: getDisplayedColumnsAssignAssets(this.currentUser),
              toolBar: {
                exportCsv: {
                  hidden: true,
                },
              },
            }),
          },
          events: [
            {
              eventName: "getData",
              handler: (_dynamicForm: any, event: GetDataEvent<Asset>) => {
                return this.assetTableService.getAssetsPost({
                  ...event,
                  queryParams: {
                    ...event.queryParams,
                    where: {
                      ...event.queryParams.where,
                      companyId: this.quote.companyId,
                      id: { $nin: this.quote.quotedAssets.map((p) => p.assetId) },
                    } as DataTableParamsWhere<Asset>,
                  },
                });
              },
            },
          ],
        },
      },
    });
  };

  private unlinkAsset = (asset: Asset, table: DataTableComponent<Asset>): void => {
    const dialogRef = this.dialog.open(DeleteModalComponent, {
      width: "300px",
      panelClass: "custom-dialog-container",
      disableClose: true,
      data: {
        name: "asset",
        warningEntity: "asset-quote",
        data: asset,
        unlink: true,
        fullUrl: `${this.quoteService.basePath}/${this.quote.id}/assets`,
        body: { assetIds: [asset.id] },
      },
    });
    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        this.getQuote();
        table.search({ isDeletingOrUnlinking: true, rows: [asset] });
      }
    });
  };

  private updateAssets = (table: DataTableComponent<Asset>): void => {
    const areAllRowsSelected = table.allRowsSelected;

    // Update properties of all assets
    const action = (propertyList: PropertyListComponent) => {
      this.loader.show();
      // As the request is made to the entity "quote" we must take the corresponding values from the search/where,
      // picking the values of "quotedAssets"
      const searchFilter = table.searchBar.first.getValues()?.["quotedAssets"] || {};
      const quotedAssetIds = table.config.selection.selectedRows.map((asset) => asset.quotedAssetId);
      const where = areAllRowsSelected ? searchFilter : { id: { $in: quotedAssetIds } };

      const getValueAndOperatorByPropertyKey = (
        key: keyof Omit<BulkUpdateQuotedAssetDto, "where" | "relations" | "quoteId">,
      ) => {
        const property = (propertyList.form.value as any[]).find((value) => value.property.key === key);

        if (property) {
          return {
            value: property?.value?.id || property?.value,
            operator: property?.operator,
          };
        } else {
          return undefined;
        }
      };

      const body: BulkUpdateQuotedAssetDto = {
        where,
        quoteId: this.quoteId,
        slaId: getValueAndOperatorByPropertyKey("slaId")?.value,
        startDate: getValueAndOperatorByPropertyKey("startDate")?.value,
        channelWorksCost: getValueAndOperatorByPropertyKey("channelWorksCost"),
        partnerPrice: getValueAndOperatorByPropertyKey("partnerPrice"),
        monitoring: getValueAndOperatorByPropertyKey("monitoring")?.value,
        dmr: getValueAndOperatorByPropertyKey("dmr")?.value,
      };

      this.quoteService
        .bulkUpdateQuotedAsset(body)
        .subscribe(
          () => {
            this.notificationService.success("Assets successfully updated");
            table.search();
          },
          (error) => this.notificationService.error(`Couldn't update assets: ${error}`),
        )
        .add(() => this.loader.hide());
    };

    const priceValidators = (operator: Operator) => {
      if (operator === "=" || operator === "-") {
        return [Validators.required, Validators.pattern(pattern.price)];
      }
      if (operator === "%") {
        return [Validators.required, Validators.pattern(pattern.percentage)];
      }
    };

    this.dialog.open<GenericModalComponent, GenericModalData<{ config: PropertyList }>>(GenericModalComponent, {
      width: "1400px",
      panelClass: ["custom-dialog-container", "mat-dialog-full-height"],
      data: {
        title: `Update ${this.quote.number} linked assets`,
        buttons: { hide: false, submit: { action } },
        component: {
          clazz: PropertyListComponent,
          inputs: {
            config: {
              properties: [
                {
                  label: "SLA",
                  key: "slaId",
                  control: "search",
                  operators: ["="],
                  search: {
                    propToDisplay: "value",
                    fetchDefaultData: true,
                    showNullOption: true,
                    service: (formValue: string | ConfigurableOption) =>
                      this.configurableOptionService.getConfigurableOptions({
                        where: {
                          ...(formValue === null && { value: null }),
                          ...((formValue as string)?.length && { value: { $cont: formValue } }),
                          ...((formValue as ConfigurableOption)?.id && {
                            value: { $cont: (formValue as ConfigurableOption).value },
                          }),
                          type: ConfigurableOptionTypes.Sla,
                        },
                      }),
                  },
                },
                {
                  label: "Start Date",
                  key: "startDate",
                  control: "date",
                  operators: ["="],
                  validators: () => [Validators.required],
                  date: {
                    min: this.dynamicForm.form.value.startDate,
                    max: this.dynamicForm.form.value.endDate,
                  },
                },
                {
                  label: "CW Cost",
                  key: "channelWorksCost",
                  control: "input",
                  operators: ["=", "-", "%"],
                  validators: priceValidators,
                },
                {
                  label: "Price",
                  key: "partnerPrice",
                  control: "input",
                  operators: ["=", "-", "%"],
                  validators: priceValidators,
                },
                {
                  label: "DMR",
                  key: "dmr",
                  control: "select",
                  operators: ["="],
                  validators: () => [Validators.required],
                  select: {
                    options: [...yesNoOptions],
                  },
                },
                {
                  label: "Monitoring",
                  key: "monitoring",
                  control: "select",
                  operators: ["="],
                  validators: () => [Validators.required],
                  select: {
                    options: [...yesNoOptions],
                  },
                },
              ],
              operators: {
                "=": { label: "Set" },
                "-": { label: "Discount by Subtraction" },
                "%": { label: "Discount by Percentage" },
              },
              summary: {
                entity: "assets",
                selectedItems: areAllRowsSelected
                  ? table.config.rows.length
                  : table.config.selection.selectedRows.length,
              },
              buttons: {
                hidden: true,
              },
            },
          },
        },
      },
    });
  };

  //#endregion

  //#region Private Methods

  private goBack = (): void => this.locationAngularCommon.back();

  private toggleManageQuote = (): void => {
    this.isEdit = !this.isEdit;
  };

  private updateQuote = (): void => {
    const data = getUpdatedValues(this.dynamicForm.form, this.quote) as Quote;

    // Company is not required
    delete data.companyId;

    if (data.endDate) {
      data.startDate = this.dynamicForm.form.get("startDate").value;
    } else if (data.startDate) {
      data.endDate = this.dynamicForm.form.get("endDate").value;
    }

    if (data) {
      this.loader.show();
      this.quoteService
        .updateQuote(this.quote.id, data)
        .subscribe(
          (quote: Quote) => {
            this.quoteId = quote.id;
            this.quote = { ...this.quote, ...quote };
            this.isEdit = false;

            this.notificationService.success("Quote updated");
            this.buildForm(this.quote);
            this.assetsTable.search();
          },
          (error) => this.notificationService.error(error || "Could not update quote"),
        )
        .add(() => this.loader.hide());
    }
  };

  private deleteQuote = (): void => {
    const dialogRef = this.dialog.open(DeleteModalComponent, {
      width: "300px",
      panelClass: "custom-dialog-container",
      disableClose: true,
      data: {
        name: "quote",
        data: this.quote,
        url: this.quoteService.basePath,
      },
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        this.router.navigate(["/quotes"]);
      }
    });
  };

  private buildForm = (quote: Quote): void => {
    this.loader.show();
    this.questionsService
      .generateQuestions({
        companyValue: quote.companyId,
        partnerValue: quote.company.partnerId,
        partnerSkip: false,
        companySkip: true,
        isManageView: true,
        editMode: true,
        showQuoteNumber: true,
        partnerDisabled: true,
        quote,
        companyEdit: false,
      })
      .subscribe((qs) => {
        this.questions = qs;

        if (this.dynamicForm) {
          this.dynamicForm.resetWithValues(this.questions);
        }
        this.loader.hide();
      });
  };

  private resetTabs = (): void => {
    setTimeout(() => this.assetsTable.resetScrollbar(), 0);
  };

  private convertQuoteAction(): void {
    const title = `Convert ${this.quote.number} to contract?`;
    const action = () => {
      this.quoteService
        .convertQuoteToContract(this.quoteId)
        .subscribe(
          () => {
            this.notificationService.success("Quote was successfully converted to a contract");
            this.getQuote({ reloadForm: true, setAssetsTab: true });
            this.assetsTable.config.selection.hidden = true;
          },
          (error) => this.notificationService.error(`Couldn't convert quote to contract: ${error}`),
        )
        .add(() => {
          this.loader.hide();
          this.dialogRef.close();
        });
    };

    this.dialogRef = this.dialog.open(GenericModalComponent, {
      width: "300px",
      panelClass: "custom-dialog-container",
      disableClose: true,
      data: {
        title,
        buttons: {
          submit: {
            name: "Convert",
            disabled: () => false,
            action,
          },
        },
      },
    });
  }
  //#endregion
}
